import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { ArrowLeft, CheckCircle2, X, Zap, Users, TrendingUp, Clock, Target, Award } from 'lucide-react'
import { usePageSEO } from '@/hooks/usePageSEO'

export const Route = createFileRoute('/comparison')({
  component: ComparisonPage,
})

function ComparisonPage() {
  const { t } = useTranslation('service')
  const mainColor = '#525FE1'
  usePageSEO({
    title: 'Comparatif JeridSchool - Logiciel scolaire pour la Tunisie',
    description:
      'Comparez JeridSchool aux outils scolaires classiques et decouvrez une plateforme complete pour les etablissements tunisiens.',
    keywords:
      'comparatif logiciel scolaire, alternative EdTrust Tunisie, plateforme scolaire Tunisie, gestion ecole privee',
    path: '/comparison',
  })

  const comparisonFeatures = [
    {
      feature: t('comparison.table.features.timetable.name'),
      category: t('comparison.table.categories.coreFunctionality'),
      normalApp: t('comparison.table.features.timetable.basic'),
      highApp: t('comparison.table.features.timetable.premium'),
      jeridSchool: t('comparison.table.features.timetable.jeridSchool'),
      icon: Clock
    },
    {
      feature: t('comparison.table.features.communication.name'),
      category: t('comparison.table.categories.collaboration'),
      normalApp: t('comparison.table.features.communication.basic'),
      highApp: t('comparison.table.features.communication.premium'),
      jeridSchool: t('comparison.table.features.communication.jeridSchool'),
      icon: Users
    },
    {
      feature: t('comparison.table.features.grading.name'),
      category: t('comparison.table.categories.assessmentTools'),
      normalApp: t('comparison.table.features.grading.basic'),
      highApp: t('comparison.table.features.grading.premium'),
      jeridSchool: t('comparison.table.features.grading.jeridSchool'),
      icon: Zap
    },
    {
      feature: t('comparison.table.features.efficiency.name'),
      category: t('comparison.table.categories.productivity'),
      normalApp: t('comparison.table.features.efficiency.basic'),
      highApp: t('comparison.table.features.efficiency.premium'),
      jeridSchool: t('comparison.table.features.efficiency.jeridSchool'),
      icon: Target
    },
    {
      feature: t('comparison.table.features.updates.name'),
      category: t('comparison.table.categories.platformEvolution'),
      normalApp: t('comparison.table.features.updates.basic'),
      highApp: t('comparison.table.features.updates.premium'),
      jeridSchool: t('comparison.table.features.updates.jeridSchool'),
      icon: Award
    },
    {
      feature: t('comparison.table.features.scalability.name'),
      category: t('comparison.table.categories.growth'),
      normalApp: t('comparison.table.features.scalability.basic'),
      highApp: t('comparison.table.features.scalability.premium'),
      jeridSchool: t('comparison.table.features.scalability.jeridSchool'),
      icon: TrendingUp
    }
  ]

  const categories = Array.from(new Set(comparisonFeatures.map(f => f.category)))

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-blue-50 to-indigo-50">
      <div className="container mx-auto px-4 py-12">
        {/* Back Button */}
        <Link
          to="/"
          className="inline-flex items-center gap-2 mb-8 text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>{t('comparison.backButton')}</span>
        </Link>

        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t('comparison.hero.title')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('comparison.hero.description')}
          </p>
        </div>

        {/* Comparison Table */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-12">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200 bg-gray-50">
                  <th className="text-left py-4 px-6 font-bold text-gray-900 w-[26%]">{t('comparison.table.headers.feature')}</th>
                  <th className="text-left py-4 px-6 font-bold text-gray-900 w-[26%]">{t('comparison.table.headers.basic')}</th>
                  <th className="text-left py-4 px-6 font-bold text-gray-900 w-[26%]">{t('comparison.table.headers.premium')}</th>
                  <th className="text-left py-4 px-6 font-bold text-yellow-600 w-[26%] bg-yellow-50">{t('comparison.table.headers.jeridSchool')}</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category) => {
                  const categoryFeatures = comparisonFeatures.filter(f => f.category === category)
                  return (
                    <React.Fragment key={category}>
                      {/* Category Header */}
                      <tr className="bg-gray-50 border-t-2 border-gray-200">
                        <td colSpan={4} className="py-3 px-6">
                          <div className="font-bold text-gray-900 text-sm uppercase tracking-wide">{category}</div>
                        </td>
                      </tr>
                      {/* Category Features */}
                      {categoryFeatures.map((item, index) => (
                        <tr 
                          key={`${category}-${index}`}
                          className="border-b border-gray-100 hover:bg-yellow-50/30 transition-colors"
                        >
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-yellow-100">
                                <item.icon className="w-4 h-4 text-yellow-600" />
                              </div>
                              <div className="font-semibold text-gray-900">{item.feature}</div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-start gap-2">
                              <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                              <span className="text-sm text-gray-600">{item.normalApp}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-start gap-2">
                              <div className="w-5 h-5 rounded-full border-2 border-blue-400 flex-shrink-0 mt-0.5"></div>
                              <span className="text-sm text-gray-600">{item.highApp}</span>
                            </div>
                          </td>
                          <td className="py-4 px-6 bg-yellow-50/30">
                            <div className="flex items-start gap-2">
                              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-yellow-600" />
                              <span className="text-sm font-medium text-yellow-600">{item.jeridSchool}</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Key Differentiators */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            {t('comparison.differentiators.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-yellow-500">
              <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('comparison.differentiators.ai.title')}</h3>
              <p className="text-gray-600">
                {t('comparison.differentiators.ai.description')}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border-l-4" style={{ borderColor: mainColor }}>
              <Users className="w-8 h-8 mb-4" style={{ color: mainColor }} />
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('comparison.differentiators.ecosystem.title')}</h3>
              <p className="text-gray-600">
                {t('comparison.differentiators.ecosystem.description')}
              </p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-yellow-500">
              <div className="w-12 h-12 rounded-lg bg-yellow-100 flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('comparison.differentiators.innovation.title')}</h3>
              <p className="text-gray-600">
                {t('comparison.differentiators.innovation.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Why Choose JeridSchool */}
        <div className="bg-gradient-to-br from-yellow-50 to-white rounded-2xl p-8 md:p-12 shadow-lg mb-12 border border-yellow-100">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            {t('comparison.whyChoose.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 bg-white rounded-lg p-4 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('comparison.whyChoose.timeSavings.title')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('comparison.whyChoose.timeSavings.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-lg p-4 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('comparison.whyChoose.costEfficiency.title')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('comparison.whyChoose.costEfficiency.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-lg p-4 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('comparison.whyChoose.betterOutcomes.title')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('comparison.whyChoose.betterOutcomes.description')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4 bg-white rounded-lg p-4 shadow-sm">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">{t('comparison.whyChoose.futureProof.title')}</h4>
                <p className="text-gray-600 text-sm">
                  {t('comparison.whyChoose.futureProof.description')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 md:p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            {t('comparison.cta.title')}
          </h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            {t('comparison.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
           
            <Link
              to="/join"
              className="px-8 py-3 border-2 border-white text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
            >
              {t('comparison.cta.scheduleDemo')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
